import { useEffect, useMemo, MutableRefObject } from "react";
import { Category } from "@lib/shared/types";
import { DomainInfo } from "src/api/domainInfo";
import { RouteConfig } from "@page-builder/ts/types";

export interface UseBlockOtherSectionsArgs {
    routeConfig: RouteConfig | null | undefined;
    domain: DomainInfo;
    category: Category | null | undefined;
    currentStep: number;
    prevStepRef: MutableRefObject<number | null>;
}

/**
 * Toggles the body `hideOtherSections` class once the form moves past step 0,
 * gated by domain / category / route config flags.
 *
 * Returns the resolved `blockOtherSections` boolean for any consumers that
 * also need it.
 */
export function useBlockOtherSections({
    routeConfig,
    domain,
    category,
    currentStep,
    prevStepRef,
}: UseBlockOtherSectionsArgs): boolean {
    const blockOtherSections: boolean = useMemo(() => {
        return Boolean(
            routeConfig?.templateConfig?.variantConfig
                ?.shouldHideSectionsAfterFirstAnswer ??
                domain?.config?.shouldHideSectionsAfterFirstAnswer ??
                category?.config?.shouldHideSectionsAfterFirstAnswer ??
                false,
        );
    }, [routeConfig, domain, category]);

    useEffect(() => {
        if (!blockOtherSections) return;

        const bodyClass = "hideOtherSections";
        if (
            currentStep > 0 ||
            (prevStepRef.current && prevStepRef.current > 0)
        ) {
            document.body.classList.add(bodyClass);
        } else {
            document.body.classList.remove(bodyClass);
        }
    }, [currentStep, blockOtherSections]);

    useEffect(() => {
        prevStepRef.current = currentStep;
    }, [currentStep]);

    return blockOtherSections;
}
